import i18next, { type i18n, type Resource } from 'i18next';
import { initReactI18next } from 'react-i18next';
import { flattenTranslationValues } from '@amzn/gsce-shared-react-lib/i18n/flatten';
import sharedDeDE from '@amzn/gsce-shared-react-lib/locales/de-DE/shared.json';
import sharedEnUS from '@amzn/gsce-shared-react-lib/locales/en-US/shared.json';
import sharedEsES from '@amzn/gsce-shared-react-lib/locales/es-ES/shared.json';
import sharedFrFR from '@amzn/gsce-shared-react-lib/locales/fr-FR/shared.json';
import sharedItIT from '@amzn/gsce-shared-react-lib/locales/it-IT/shared.json';
import sharedJaJP from '@amzn/gsce-shared-react-lib/locales/ja-JP/shared.json';
import sharedPlPL from '@amzn/gsce-shared-react-lib/locales/pl-PL/shared.json';
import sharedPtBR from '@amzn/gsce-shared-react-lib/locales/pt-BR/shared.json';
import { DEFAULT_LOCALE } from '../../../shared/i18n/locales';

/** i18next namespace the shared lib's components translate from. */
const SHARED_NAMESPACE = 'shared';

/**
 * Shared-lib locale packs for every locale Gravion lets the user pick, keyed
 * by the lib's own locale tag. Inlined (not fetched) for the same reason the
 * arb-tools bundles are in `createMobileMbm`: no runtime fetch, no dev-server
 * middleware, and the panel is translated on first paint.
 */
const SHARED_RESOURCES: Record<string, unknown> = {
  'de-DE': sharedDeDE,
  'en-US': sharedEnUS,
  'es-ES': sharedEsES,
  'fr-FR': sharedFrFR,
  'it-IT': sharedItIT,
  'ja-JP': sharedJaJP,
  'pl-PL': sharedPlPL,
  'pt-BR': sharedPtBR,
};

/**
 * Gravion offers two locales the shared lib doesn't ship. Route them to the
 * nearest sibling before the English default so the panel still reads in the
 * user's language.
 */
const FALLBACK_LOCALES = {
  'fr-CA': ['fr-FR', DEFAULT_LOCALE],
  'pt-PT': ['pt-BR', DEFAULT_LOCALE],
  'default': [DEFAULT_LOCALE],
};

/**
 * Builds the i18next instance that backs the shared `AgentChatPanel`.
 *
 * The rest of the mobile shell translates through arb-tools; the shared lib
 * translates through react-i18next. This instance is scoped to the chat
 * surface (created once by `AstraMobileProvider`, never the global singleton)
 * and carries only the lib's `shared` namespace, so the two i18n stacks
 * coexist without either leaking into the other.
 *
 * Deliberately not the lib's `createI18nInstance`: that factory wires a
 * browser language detector and an HTTP backend — optional peer deps Gravion
 * doesn't install, and behaviour we don't want (the locale comes from the
 * user's saved preference, and every pack is bundled).
 */
export function createAstraI18n(locale: string): i18n {
  const instance = i18next.createInstance();
  const resources = Object.fromEntries(
    Object.entries(SHARED_RESOURCES).map(([tag, pack]) => [
      tag,
      { [SHARED_NAMESPACE]: flattenTranslationValues(pack) },
    ]),
  ) as Resource;

  // Synchronous: every resource is bundled and no backend is registered, so
  // `init` resolves before the first render and the returned promise carries
  // nothing we need.
  void instance.use(initReactI18next).init({
    lng: locale,
    fallbackLng: FALLBACK_LOCALES,
    // Only ever resolve the exact tag (plus the fallbacks above) — don't
    // also probe the bare language ('fr'), which no pack is keyed by.
    load: 'currentOnly',
    defaultNS: SHARED_NAMESPACE,
    ns: [SHARED_NAMESPACE],
    resources,
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
    returnNull: false,
  });

  return instance;
}
